import qs from 'qs'
import config from 'config'
import { AxiosError } from 'axios'
import { ServiceError, NotFoundError, InvalidResponseError } from 'services/errors'
import { PAGE_ACTION_TYPES } from 'client/constants'
import { instrumenter } from 'client/utils/instrumentation'
import type { DesignPersonalizationContext } from '@vp/personalization-types'

import { AbstractService } from 'services/AbstractService'
import { TEMPLATE_PURPOSES } from '~/shared/constants'
import getLogger from 'shared/logger'
/* eslint-disable @typescript-eslint/no-explicit-any */
//TODO MIGRATION T011: Fix eslint errors

export type ContentQuery = {
  galleryName: string
  bypassApproval: Gallery.Models.Url.ValidParsedQsValue<boolean>
  locale: i18n.Locale
  mpvId: Gallery.Models.Url.ValidParsedQsValue<string>
  offset?: number
  length?: number
  attributes?: string[][]
  categories?: string[][]
  facetCategories?: string[]
  keyword?: string[]
  collection?: string
  selectedOptions?: Record<string, any>
  designIds?: string[]
  // Note that currently accessing a DPC in content causes issues with favorites
  // As we save the diamond preview urls to WES
  // Do not enable until this is solved somehow
  designPersonalizationContext?: DesignPersonalizationContext
  designPersonalizationContextId?: string
  noCache: Gallery.Models.Url.ValidParsedQsValue<boolean>
  useConstraints: Gallery.Models.Url.ValidParsedQsValue<boolean>
  forcedRankingStrategyOverride: Gallery.Models.Url.ValidParsedQsValue<string>
  renderProps: Record<string, string | null | undefined>
  templatePurposes?: TEMPLATE_PURPOSES[]
  searchBackend?: Gallery.Models.Url.ValidParsedQsValue<string>
  experimentFlags: Record<string, string>
  configExperimentFlags: string[]
  designCreationTypes?: Gallery.Models.Url.ValidParsedQsValue<string[]>
  debug?: boolean
  includeCategoryAndKeywordData?: boolean
  boostedKeywords?: Gallery.ContentQuery.Boost[]
  boostedCategories?: Gallery.ContentQuery.Boost[]
  boostedSearchableEnumeratedData?: Gallery.ContentQuery.BoostedSearchableEnumeratedData[]
  filterAltTextCompatibleTagging?: boolean
  moreLike?: string[]
  sets?: Gallery.Models.Url.ValidParsedQsValue<Gallery.Models.Url.JSONObject[]>
}

const DEFAULT_CONTENT_QUERY = {} as ContentQuery

export function calculateOffset(page: number, pageSize: number): number {
  return (page - 1) * pageSize
}

const paramsSerializer = (complexObjectKey: string) => {
  return (params: any) => {
    const complexValue = params[complexObjectKey]

    const { [complexObjectKey]: _, ...otherParams } = params

    let queryString = qs.stringify(otherParams, { arrayFormat: 'repeat' })

    // Manually append complex object parameters to avoid qs.stringify issues
    if (Array.isArray(complexValue) && complexValue.length > 0) {
      complexValue.forEach((item: any) => {
        queryString += `&${complexObjectKey}=${encodeURIComponent(JSON.stringify(item))}`
      })
    }

    return queryString
  }
}

const formatBoostParams = (boosts: Array<{ value: string; weight: number }> | undefined): string =>
  boosts?.map((boost) => `${boost.value}:${boost.weight}`).join(',') ?? ''

const formatEnumeratedBoostParams = (boosts: Array<{ key: string; value: string; weight: number }> | undefined): string =>
  boosts?.map((boost) => `${boost.key}:${boost.value}:${boost.weight.toFixed(2)}`).join(',') ?? ''

export class ContentQueryApi extends AbstractService implements Services.IContentService {
  /**
   * Given a ContentQuery object, request new content from the ContentService
   *
   * @param query
   * @throws ServiceError
   */
  public async getContent(query: ContentQuery = DEFAULT_CONTENT_QUERY): Promise<Gallery.ContentQuery.Response> {
    const {
      attributes = [],
      bypassApproval,
      categories = [],
      locale,
      facetCategories,
      galleryName,
      keyword,
      collection,
      length,
      offset,
      selectedOptions,
      noCache,
      useConstraints,
      forcedRankingStrategyOverride,
      designIds,
      renderProps,
      templatePurposes,
      searchBackend,
      experimentFlags,
      configExperimentFlags,
      designCreationTypes,
      debug,
      includeCategoryAndKeywordData,
      boostedKeywords = [],
      boostedCategories = [],
      boostedSearchableEnumeratedData = [],
      sets,
      ...rest
    } = query

    if (!galleryName) {
      throw new Error('Parameter "galleryName" must be provided')
    }

    const url = `v2/Galleries/${encodeURIComponent(galleryName)}/Culture/${locale}/Content`

    try {
      const response = await this.api.get<Gallery.ContentQuery.Response>(url, {
        params: {
          attributes: attributes.map((attributeItem) => attributeItem.join(',')),
          bypassApproval,
          categories: categories.map((categoryItem) => categoryItem.join(',')),
          facetCategories,
          keywords: [keyword],
          collection,
          limit: length,
          offset,
          requestor: noCache ? config.noCacheRequestor : config.appName,
          noCache,
          useConstraints,
          forcedRankingStrategyOverride: boostedSearchableEnumeratedData?.length > 0 ? 'null' : forcedRankingStrategyOverride,
          selectedOptions,
          designIds: designIds?.join(','),
          renderProps,
          templatePurposes,
          searchBackend,
          experimentFlags,
          configExperimentFlags,
          designCreationTypes,
          debug,
          includeCategoryAndKeywordData,
          ...(boostedKeywords?.length && {
            boostedKeywords: formatBoostParams(boostedKeywords),
          }),
          ...(boostedCategories?.length && {
            boostedCategories: formatBoostParams(boostedCategories),
          }),
          ...(boostedSearchableEnumeratedData?.length && {
            boostedSearchableEnumeratedData: formatEnumeratedBoostParams(boostedSearchableEnumeratedData),
          }),
          sets,
          ...rest,
        },
        paramsSerializer: paramsSerializer('sets'),
      })

      if (!response || !response.data || typeof response.data === 'string') {
        throw new InvalidResponseError({
          message: `Empty Response from content query. Status: ${response?.status}`,
          query,
          responseStatus: response?.status,
          responseData: response?.data,
          url,
        })
      }

      // don't log a warning for an empty keyword search
      if (!keyword && response.data.results.content.length <= 0) {
        this.logger.warn(
          new ServiceError({
            url,
            message: 'Zero content returned from content API',
            query,
          }),
        )
      }

      const resp = response.data
      resp.requestUrl = url

      return resp
    } catch (e) {
      if ((e as AxiosError).response && (e as AxiosError).response?.status === 404) {
        const request = (e as AxiosError).request
        const requestUrl = `${request?.protocol}//${request?.host}${request?.path}`
        const notFoundError = new NotFoundError(
          {
            galleryName,
            locale,
            url,
            message: `Content Query 404 response - ${galleryName} ${locale} (${requestUrl})`,
            info: (e as Error).message,
          },
          e as Error,
        )

        instrumenter.recordPageAction(PAGE_ACTION_TYPES.CONTENT_LOAD_FAILED, {
          galleryName,
          info: notFoundError,
        })

        throw notFoundError
      }

      if ((e as Error).name === 'InvalidResponseError') {
        throw e
      }

      throw new ServiceError(
        {
          url,
          message: `Bad response from content query service: ${(e as Error).message}`,
          query,
        },
        e as Error,
      )
    }
  }

  /**
   * Retrieves matching templates for a specific product and template identifier
   *
   * @param productKey - The product key to find matching templates for
   * @param culture - The culture/locale to use
   * @param templateIdentifier - The template identifier to match
   * @returns Promise with array of matching templates
   * @throws ServiceError
   */
  public async getMatchingTemplatesForProduct(
    productKey: string,
    culture: string,
    templateIdentifier: string,
  ): Promise<Gallery.ContentQuery.MatchingResponse[]> {
    if (!productKey) {
      throw new Error('Parameter "productKey" must be provided')
    }
    if (!templateIdentifier) {
      throw new Error('Parameter "templateIdentifier" must be provided')
    }

    const url = `v2/Product/${productKey}/Culture/${culture}/matching`

    try {
      const response = await this.api.get<Gallery.ContentQuery.MatchingResponse[]>(url, {
        params: {
          requestor: config.appName,
          templateIdentifier,
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      })

      if (!response || !response.data || typeof response.data === 'string') {
        return []
      }

      return response.data
    } catch (e) {
      if ((e as Error).name === 'InvalidResponseError') {
        throw e
      }
      return []
    }
  }
}

export const ContentService = new ContentQueryApi(config.services.contentQueryService, getLogger)
